export type WorksheetRefStatsCacheEntry = {
  pageCount: number;
  documentBytes: number;
};

const STORAGE_PREFIX = 'cp:program-worksheet-list-stats:v1:';

const memoryByWorkspace = new Map<string, Record<string, WorksheetRefStatsCacheEntry>>();

function draftStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function loadRecord(workspaceId: string): Record<string, WorksheetRefStatsCacheEntry> {
  const sid = workspaceId.trim();
  if (!sid) return {};
  const mem = memoryByWorkspace.get(sid);
  if (mem) return mem;
  const storage = draftStorage();
  if (!storage) {
    memoryByWorkspace.set(sid, {});
    return {};
  }
  try {
    const raw = storage.getItem(STORAGE_PREFIX + sid);
    const parsed = raw ? (JSON.parse(raw) as Record<string, WorksheetRefStatsCacheEntry>) : {};
    const normalized = parsed && typeof parsed === 'object' ? parsed : {};
    memoryByWorkspace.set(sid, normalized);
    return normalized;
  } catch {
    memoryByWorkspace.set(sid, {});
    return {};
  }
}

function saveRecord(workspaceId: string, record: Record<string, WorksheetRefStatsCacheEntry>): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  memoryByWorkspace.set(sid, record);
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_PREFIX + sid, JSON.stringify(record));
  } catch {
    /* quota 등은 무시 */
  }
}

/** 캐시 hit인 refId만 즉시 반환 — 나머지(miss)는 호출부에서 DB로 채운다 */
export function readProgramWorksheetListStatsCache(
  workspaceId: string,
  refIds: string[]
): Map<string, WorksheetRefStatsCacheEntry> {
  const sid = workspaceId.trim();
  const out = new Map<string, WorksheetRefStatsCacheEntry>();
  if (!sid || !refIds.length) return out;
  const record = loadRecord(sid);
  refIds.forEach((key) => {
    const hit = record[key];
    if (hit && Number.isFinite(hit.pageCount) && Number.isFinite(hit.documentBytes)) {
      out.set(key, { pageCount: hit.pageCount, documentBytes: hit.documentBytes });
    }
  });
  return out;
}

export function writeProgramWorksheetListStatsCache(
  workspaceId: string,
  patch: Map<string, WorksheetRefStatsCacheEntry> | Record<string, WorksheetRefStatsCacheEntry>
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const prev = loadRecord(sid);
  const next = { ...prev };
  if (patch instanceof Map) {
    patch.forEach((stats, key) => {
      next[key] = stats;
    });
  } else {
    Object.entries(patch).forEach(([key, stats]) => {
      next[key] = stats;
    });
  }
  saveRecord(sid, next);
}

/** 특정 refId만 제거 — 활동지 저장/삭제 후 재조회 유도 */
export function invalidateProgramWorksheetListStatsCacheKeys(workspaceId: string, refIds: string[]): void {
  const sid = workspaceId.trim();
  if (!sid || !refIds.length) return;
  const prev = loadRecord(sid);
  const next = { ...prev };
  let changed = false;
  refIds.forEach((key) => {
    if (key in next) {
      delete next[key];
      changed = true;
    }
  });
  if (changed) saveRecord(sid, next);
}

export function clearProgramWorksheetListStatsCache(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  memoryByWorkspace.delete(sid);
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_PREFIX + sid);
  } catch {
    /* ignore */
  }
}
